import type { ViewerRouteKind } from "../../../utils/viewerRouteContext";
import type {
  AdminAccessState,
  ViewerManifestStatus,
} from "../../../types/viewerDeployment";
import type { ViewerAdminRuntimeUiState } from "./viewerAdminRuntimeUiState";

export type ViewerShellBlockingReason =
  | "none"
  | "manifest_loading"
  | "manifest_error"
  | "admin_access_denied"
  | "admin_runtime_loading"
  | "admin_runtime_error"
  | "brand_loading"
  | "model_loading"
  | "model_error"
  | "runtime_model_error";

export interface ViewerShellBlockingState {
  reason: ViewerShellBlockingReason;
  message: string | null;
  includeAdminRuntimeBridge: boolean;
}

interface ResolveViewerShellBlockingStateParams {
  routeKind: ViewerRouteKind;
  manifestStatus: ViewerManifestStatus;
  manifestError: string | null;
  adminAccessState: AdminAccessState;
  adminRuntimeUiState: ViewerAdminRuntimeUiState;
  isBrandConfigLoading: boolean;
  isModelConfigLoading: boolean;
  modelConfigError: string | null;
  runtimeModelError: string | null;
}

export function resolveViewerShellBlockingState(
  params: ResolveViewerShellBlockingStateParams,
): ViewerShellBlockingState {
  if (params.routeKind !== "unknown" && params.manifestStatus === "loading") {
    return {
      reason: "manifest_loading",
      message: null,
      includeAdminRuntimeBridge: false,
    };
  }

  if (params.routeKind !== "unknown" && params.manifestStatus === "error") {
    return {
      reason: "manifest_error",
      message: params.manifestError || "Failed to load viewer manifest.",
      includeAdminRuntimeBridge: false,
    };
  }

  if (params.routeKind === "admin" && params.adminAccessState === "denied") {
    return {
      reason: "admin_access_denied",
      message: "Admin access denied.",
      includeAdminRuntimeBridge: false,
    };
  }

  if (params.adminRuntimeUiState.blockingView === "loading") {
    return {
      reason: "admin_runtime_loading",
      message: null,
      includeAdminRuntimeBridge: true,
    };
  }

  if (
    params.adminRuntimeUiState.blockingView === "error" &&
    params.adminRuntimeUiState.blockingErrorMessage
  ) {
    return {
      reason: "admin_runtime_error",
      message: params.adminRuntimeUiState.blockingErrorMessage,
      includeAdminRuntimeBridge: true,
    };
  }

  if (params.isBrandConfigLoading) {
    return {
      reason: "brand_loading",
      message: null,
      includeAdminRuntimeBridge: false,
    };
  }

  if (params.isModelConfigLoading) {
    return {
      reason: "model_loading",
      message: null,
      includeAdminRuntimeBridge: false,
    };
  }

  if (params.modelConfigError) {
    return {
      reason: "model_error",
      message: params.modelConfigError,
      includeAdminRuntimeBridge: false,
    };
  }

  if (params.runtimeModelError) {
    return {
      reason: "runtime_model_error",
      message: params.runtimeModelError,
      includeAdminRuntimeBridge: false,
    };
  }

  return {
    reason: "none",
    message: null,
    includeAdminRuntimeBridge: false,
  };
}
